import { useMemo, useState } from "react";
import {
  resetRestaurantDemoState,
  saveRestaurantDemoState,
  type RestaurantDemoState,
} from "../sales-playground/restaurant-sandbox";
import {
  createRestaurantDemoV5State,
  resetRestaurantDemoV5State,
  saveRestaurantDemoV5State,
  type RestaurantDemoV5State,
} from "../sales-playground/restaurant-demo-v5-state";
import {
  RESTAURANT_COMMERCIAL_ADMIN_SECTIONS,
  restaurantCommercialCustomerPath,
  type RestaurantCommercialAdminSection,
} from "./restaurant-commercial-routing";
import { ensureRestaurantCommercialCatalog, ensureRestaurantCommercialV5 } from "./restaurant-commercial-catalog";
import { RestaurantCommercialMenuAdmin } from "./RestaurantCommercialMenuAdmin";
import { RestaurantCommercialOrdersAdmin } from "./RestaurantCommercialOrdersAdmin";
import { RestaurantCommercialReservationsAdmin, RestaurantCommercialTablesAdmin } from "./RestaurantCommercialSchedulingAdmin";
import type { RestaurantCommercialAdminPanelProps } from "./restaurant-commercial-admin-types";
import "./restaurant-commercial-admin-depth.css";

const SECTION_LABELS: Readonly<Record<RestaurantCommercialAdminSection, { label: string; icon: string; title: string; description: string }>> = Object.freeze({
  carta: { label: "Carta", icon: "≡", title: "Carta", description: "Productos, categorías, precios, opciones y disponibilidad del menú digital." },
  mesas: { label: "Mesas", icon: "▦", title: "Mesas", description: "Plano, Timeline, capacidad y estado operativo de cada mesa." },
  reservas: { label: "Reservas", icon: "◷", title: "Reservas", description: "Reservas web y manuales, asignación de mesas y gestión de cada servicio." },
  pedidos: { label: "Pedidos y cocina", icon: "▤", title: "Pedidos y cocina", description: "Del pedido nuevo a preparación, listo y entregado." },
});

function sectionFromUrl(): RestaurantCommercialAdminSection {
  const requested = new URLSearchParams(window.location.search).get("section") as RestaurantCommercialAdminSection | null;
  return requested && RESTAURANT_COMMERCIAL_ADMIN_SECTIONS.includes(requested) ? requested : "pedidos";
}

export function RestaurantCommercialAdmin({
  businessSlug,
  storageSlug,
  businessName,
  navigate,
}: {
  businessSlug: string;
  storageSlug: string;
  businessName: string;
  navigate: (path: string) => void;
}) {
  const initialState = useMemo(() => ensureRestaurantCommercialCatalog(storageSlug, businessName), [storageSlug, businessName]);
  const initialV5 = useMemo(() => ensureRestaurantCommercialV5(storageSlug, initialState), [storageSlug, initialState]);
  const [state, setRawState] = useState<RestaurantDemoState>(initialState);
  const [v5, setRawV5] = useState<RestaurantDemoV5State>(initialV5);
  const [section, setSection] = useState<RestaurantCommercialAdminSection>(sectionFromUrl());
  const [toast, setToast] = useState<{ message: string; tone: "success" | "danger" } | null>(null);

  function setState(next: RestaurantDemoState) {
    const normalized = Object.freeze({ ...next, businessName });
    saveRestaurantDemoState(storageSlug, normalized);
    setRawState(normalized);
  }

  function setV5(next: RestaurantDemoV5State) {
    saveRestaurantDemoV5State(storageSlug, next);
    setRawV5(next);
  }

  function notify(message: string, tone: "success" | "danger" = "success") {
    setToast({ message, tone });
    window.setTimeout(() => setToast((current) => current?.message === message ? null : current), 2400);
  }

  function changeSection(next: RestaurantCommercialAdminSection) {
    setSection(next);
    const params = new URLSearchParams(window.location.search);
    params.set("view", "admin");
    params.set("section", next);
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  }

  function reset() {
    if (!window.confirm("¿Restablecer todos los datos de esta demo comercial?")) return;
    resetRestaurantDemoState(storageSlug, businessName);
    resetRestaurantDemoV5State(storageSlug);
    const next = ensureRestaurantCommercialCatalog(storageSlug, businessName);
    const nextV5 = createRestaurantDemoV5State(next);
    saveRestaurantDemoV5State(storageSlug, nextV5);
    setRawState(next);
    setRawV5(nextV5);
    notify("Demo restablecida");
  }

  const panelProps: RestaurantCommercialAdminPanelProps = { state, setState, v5, setV5, notify };
  const heading = SECTION_LABELS[section];
  const activeOrders = state.orders.filter((order) => ["submitted", "preparing", "ready"].includes(order.status)).length;

  return <div className="rcd-admin-shell rcd-admin-shell-depth">
    <aside className="rcd-admin-sidebar">
      <div className="rcd-admin-brand"><span>CD</span><div><strong>{businessName}</strong><small>Gestión del restaurante</small></div></div>
      <nav>{RESTAURANT_COMMERCIAL_ADMIN_SECTIONS.map((item) => <button key={item} type="button" className={section === item ? "active" : ""} onClick={() => changeSection(item)}><i>{SECTION_LABELS[item].icon}</i><span>{SECTION_LABELS[item].label}</span>{item === "pedidos" && activeOrders ? <b>{activeOrders}</b> : null}</button>)}</nav>
      <div className="rcd-admin-sidebar-footer"><button type="button" onClick={() => navigate(restaurantCommercialCustomerPath(businessSlug))}>Ver sitio del restaurante ↗</button><button type="button" onClick={reset}>Restablecer demo</button></div>
    </aside>
    <main className="rcd-admin-main">
      <header className="rcd-admin-topbar"><div><span className="rcd-eyebrow">Demo comercial · {businessName}</span><h1>{heading.title}</h1><p>{heading.description}</p></div><button className="rcd-secondary" type="button" onClick={() => navigate(restaurantCommercialCustomerPath(businessSlug))}>Ver experiencia cliente</button></header>
      <div className="rcd-admin-content">{section === "carta" ? <RestaurantCommercialMenuAdmin {...panelProps} /> : null}{section === "mesas" ? <RestaurantCommercialTablesAdmin {...panelProps} /> : null}{section === "reservas" ? <RestaurantCommercialReservationsAdmin {...panelProps} /> : null}{section === "pedidos" ? <RestaurantCommercialOrdersAdmin {...panelProps} /> : null}</div>
    </main>
    {toast ? <div className={`rcd-toast ${toast.tone === "danger" ? "danger" : ""}`} role={toast.tone === "danger" ? "alert" : "status"}>{toast.tone === "success" ? "✓ " : "! "}{toast.message}</div> : null}
  </div>;
}
